// Agencies API — typed functions over the admin backend contract. Consumed
// verbatim; no invented fields.
//
//   GET  /admin/agencies       — { agencies: AgencyRecord[] }
//   POST /admin/agencies       — create (201 = the full AgencyRecord)
//   PUT  /admin/agencies/{id}  — full-record replace (200 = the updated record)
//
// CONTRACT NOTE (regression-pinned server-side): PUT replaces ALL fields — the
// FE must always send the complete AgencyBody; any omitted field becomes ""/
// empty on the backend. `agencyToBody` exists precisely so an edit surface can
// derive a complete body from a fetched record without dropping fields.
//
// fee_pct is a decimal STRING ("" = unknown/NULL). geos is always [] in
// responses, never null.

import { api } from "./client";

// The write shape (POST body, PUT body) — everything except id/created_at.
export interface AgencyBody {
  name: string;
  telegram: string;
  financial_conditions: string;
  fee_pct: string;
  role_scope: string;
  status: string;
  cooperation_link: string;
  notes: string;
  // Business Center id ("" = not set). Free text — optionally pickable from
  // GET /admin/tiktok/bcs when that endpoint is available.
  bc_id: string;
  geos: string[];
}

// The read shape (GET rows, POST 201, PUT 200).
export interface AgencyRecord extends AgencyBody {
  id: string;
  // Unix seconds.
  created_at: number;
}

export const EMPTY_AGENCY: AgencyBody = {
  name: "",
  telegram: "",
  financial_conditions: "",
  fee_pct: "",
  role_scope: "",
  status: "",
  cooperation_link: "",
  notes: "",
  bc_id: "",
  geos: [],
};

// Derive a COMPLETE write body from a fetched record, defaulting any missing
// field to ""/[] so a PUT never accidentally clears data by omission.
export function agencyToBody(a: AgencyRecord): AgencyBody {
  return {
    name: a.name ?? "",
    telegram: a.telegram ?? "",
    financial_conditions: a.financial_conditions ?? "",
    fee_pct: a.fee_pct ?? "",
    role_scope: a.role_scope ?? "",
    status: a.status ?? "",
    cooperation_link: a.cooperation_link ?? "",
    notes: a.notes ?? "",
    bc_id: a.bc_id ?? "",
    geos: a.geos ?? [],
  };
}

export function listAgencies(): Promise<{ agencies: AgencyRecord[] }> {
  return api.get<{ agencies: AgencyRecord[] }>("/admin/agencies");
}

export function createAgency(body: AgencyBody): Promise<AgencyRecord> {
  return api.post<AgencyRecord>("/admin/agencies", body);
}

// Full-record replace — `body` MUST be complete (see the contract note above).
export function updateAgency(
  id: string,
  body: AgencyBody,
): Promise<AgencyRecord> {
  return api.put<AgencyRecord>(`/admin/agencies/${id}`, body);
}
